import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  Divider,
  Grid,
  Collapse,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  Card,
  CardContent,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import {
  Close as CloseIcon,
  Menu as MenuIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Engineering as EngineeringIcon,
  AcUnit as AcUnitIcon,
  Whatshot as WhatshotIcon,
  Architecture as ArchitectureIcon
} from '@mui/icons-material';
import DXFViewer from './MEPToolkit/DXFViewer';
import { API_CONFIG } from '../config/apiConfig';

interface HVACVariables {
  // Building Parameters
  grossAreaSqft: number;
  netConditionedAreaSqft: number;
  buildingPerimeterFt: number;
  ceilingHeightFt: number;

  // Climate Design Conditions
  climateZone: string;
  locationCity?: string;
  locationState?: string;
  elevationFt: number;
  summerOutdoorDbF: number;
  summerOutdoorWbF: number;
  winterOutdoorDbF: number;
  indoorCoolingTempF: number;
  indoorHeatingTempF: number;
  indoorSummerRhPercent: number;
  indoorWinterRhPercent: number;

  // Envelope Parameters
  wallRValue: number;
  wallUValue: number;
  roofRValue: number;
  roofUValue: number;
  windowUValue: number;
  windowShgc: number;
  windowToWallRatio: number;
  infiltrationAch: number;

  // Internal Gains
  occupancyDensityPer1000Sqft: number;
  occupancySensibleBtuPerPerson: number;
  occupancyLatentBtuPerPerson: number;
  lightingWattsPerSqft: number;
  equipmentWattsPerSqft: number;

  // Ventilation Parameters (ASHRAE 62.1 Compliant)
  ventilationOfficeCfmPerPerson: number;
  ventilationOfficeCfmPerSqft: number;
  ventilationConferenceCfmPerPerson: number;
  ventilationConferenceCfmPerSqft: number;
  ventilationConferenceOccupancyPer1000Sqft: number;
  ventilationCommonCfmPerSqft: number;

  // Additional Space Type Ventilation
  ventilationReceptionCfmPerPerson: number;
  ventilationReceptionCfmPerSqft: number;
  ventilationRestaurantCfmPerPerson: number;
  ventilationRestaurantCfmPerSqft: number;
  ventilationRetailCfmPerPerson: number;
  ventilationRetailCfmPerSqft: number;
  ventilationClassroomCfmPerPerson: number;
  ventilationClassroomCfmPerSqft: number;
  ventilationLibraryCfmPerPerson: number;
  ventilationLibraryCfmPerSqft: number;
  ventilationLobbysCfmPerPerson: number;
  ventilationLobbysCfmPerSqft: number;
  ventilationCafeteriaCfmPerPerson: number;
  ventilationCafeteriaCfmPerSqft: number;

  // Space Distribution
  spaceOfficePercent: number;
  spaceConferencePercent: number;
  spaceCommonPercent: number;
  spaceReceptionPercent: number;
  spaceRestaurantPercent: number;
  spaceRetailPercent: number;
  spaceClassroomPercent: number;
  spaceLibraryPercent: number;
  spaceLobbyPercent: number;
  spaceCafeteriaPercent: number;

  // Calculation Factors
  solarHeatGainFactorBtuPerSqft: number;
  airDensityCorrectionFactor: number;
  coolingSafetyFactorPercent: number;
  heatingSafetyFactorPercent: number;
}

interface HVACCalculationPanelProps {
  calculationId: string;
  projectId?: string;
  onClose: () => void;
  authToken: string;
}

const HVACCalculationPanel: React.FC<HVACCalculationPanelProps> = ({
  calculationId,
  projectId,
  onClose,
  authToken
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [variables, setVariables] = useState<HVACVariables | null>(null);
  const [modifiedVariables, setModifiedVariables] = useState<Partial<HVACVariables>>({});
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [ieccYear, setIeccYear] = useState<'2018' | '2021'>('2021');
  const [umcYear, setUmcYear] = useState<'2018' | '2021'>('2018');
  const [locationSearch, setLocationSearch] = useState<string>('');
  const [searchingLocation, setSearchingLocation] = useState(false);

  const loadCalculationData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('Loading HVAC calculation data:', { calculationId, authToken: authToken ? 'present' : 'missing' });

      if (!calculationId) {
        throw new Error('No calculation ID provided');
      }

      // Use direct service URL with CORS mode
      const MEP_SERVICE_URL = API_CONFIG.MEP_SERVICE_URL;
      const response = await fetch(`${MEP_SERVICE_URL}/api/v1/hvac/report/${calculationId}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        mode: 'cors'
      });

      console.log('HVAC report response:', {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type')
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
          throw new Error(`Server returned HTML instead of JSON (status: ${response.status})`);
        }
        throw new Error(`Failed to load calculation (status: ${response.status})`);
      }

      const data = await response.json();
      console.log('HVAC Report Data Structure:', data);
      setReportData(data);

      // Check if data has the expected structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid report data structure');
      }

      // Extract variables from report data with safe navigation
      const vars: HVACVariables = {
        grossAreaSqft: data.buildingSummary?.grossArea || 0,
        netConditionedAreaSqft: data.buildingSummary?.netConditionedArea || 0,
        buildingPerimeterFt: data.buildingSummary?.perimeter || 0,
        ceilingHeightFt: data.buildingSummary?.ceilingHeight || 10,
        climateZone: data.designConditions?.location?.climateZone || '5B',
        locationCity: data.designConditions?.location?.city || '',
        locationState: data.designConditions?.location?.state || '',
        elevationFt: data.designConditions?.location?.elevation || 0,
        summerOutdoorDbF: data.designConditions?.outdoor?.summerDbF || 95,
        summerOutdoorWbF: data.designConditions?.outdoor?.summerWbF || 75,
        winterOutdoorDbF: data.designConditions?.outdoor?.winterDbF || 20,
        indoorCoolingTempF: data.designConditions?.indoor?.coolingTempF || 75,
        indoorHeatingTempF: data.designConditions?.indoor?.heatingTempF || 70,
        indoorSummerRhPercent: data.designConditions?.indoor?.summerRhPercent || 50,
        indoorWinterRhPercent: data.designConditions?.indoor?.winterRhPercent || 30,
        wallRValue: data.envelopeParameters?.wall?.rValue || 19,
        wallUValue: data.envelopeParameters?.wall?.uValue || 0.053,
        roofRValue: data.envelopeParameters?.roof?.rValue || 30,
        roofUValue: data.envelopeParameters?.roof?.uValue || 0.033,
        windowUValue: data.envelopeParameters?.windows?.uValue || 0.35,
        windowShgc: data.envelopeParameters?.windows?.shgc || 0.30,
        windowToWallRatio: data.envelopeParameters?.windows?.windowToWallRatio || 0.30,
        infiltrationAch: data.envelopeParameters?.infiltration?.ach || 0.50,
        occupancyDensityPer1000Sqft: data.internalGains?.occupancy?.densityPer1000Sqft || 5,
        occupancySensibleBtuPerPerson: data.internalGains?.occupancy?.sensibleBtuPerPerson || 250,
        occupancyLatentBtuPerPerson: data.internalGains?.occupancy?.latentBtuPerPerson || 200,
        lightingWattsPerSqft: data.internalGains?.lighting?.wattsPerSqft || 1.0,
        equipmentWattsPerSqft: data.internalGains?.equipment?.wattsPerSqft || 1.5,
        ventilationOfficeCfmPerPerson: data.ventilationParameters?.office?.cfmPerPerson || 5,
        ventilationOfficeCfmPerSqft: data.ventilationParameters?.office?.cfmPerSqft || 0.06,
        ventilationConferenceCfmPerPerson: data.ventilationParameters?.conference?.cfmPerPerson || 5,
        ventilationConferenceCfmPerSqft: data.ventilationParameters?.conference?.cfmPerSqft || 0.06,
        ventilationConferenceOccupancyPer1000Sqft: data.ventilationParameters?.conference?.occupancyPer1000Sqft || 50,
        ventilationCommonCfmPerSqft: data.ventilationParameters?.common?.cfmPerSqft || 0.06,

        // Additional space type ventilation parameters with ASHRAE 62.1 defaults
        ventilationReceptionCfmPerPerson: data.ventilationParameters?.reception?.cfmPerPerson || 5,
        ventilationReceptionCfmPerSqft: data.ventilationParameters?.reception?.cfmPerSqft || 0.06,
        ventilationRestaurantCfmPerPerson: data.ventilationParameters?.restaurant?.cfmPerPerson || 7.5,
        ventilationRestaurantCfmPerSqft: data.ventilationParameters?.restaurant?.cfmPerSqft || 0.18,
        ventilationRetailCfmPerPerson: data.ventilationParameters?.retail?.cfmPerPerson || 7.5,
        ventilationRetailCfmPerSqft: data.ventilationParameters?.retail?.cfmPerSqft || 0.12,
        ventilationClassroomCfmPerPerson: data.ventilationParameters?.classroom?.cfmPerPerson || 10,
        ventilationClassroomCfmPerSqft: data.ventilationParameters?.classroom?.cfmPerSqft || 0.12,
        ventilationLibraryCfmPerPerson: data.ventilationParameters?.library?.cfmPerPerson || 5,
        ventilationLibraryCfmPerSqft: data.ventilationParameters?.library?.cfmPerSqft || 0.12,
        ventilationLobbysCfmPerPerson: data.ventilationParameters?.lobby?.cfmPerPerson || 7.5,
        ventilationLobbysCfmPerSqft: data.ventilationParameters?.lobby?.cfmPerSqft || 0.06,
        ventilationCafeteriaCfmPerPerson: data.ventilationParameters?.cafeteria?.cfmPerPerson || 7.5,
        ventilationCafeteriaCfmPerSqft: data.ventilationParameters?.cafeteria?.cfmPerSqft || 0.18,

        spaceOfficePercent: data.spaceDistribution?.officePercent || 60,
        spaceConferencePercent: data.spaceDistribution?.conferencePercent || 10,
        spaceCommonPercent: data.spaceDistribution?.commonPercent || 30,
        spaceReceptionPercent: data.spaceDistribution?.receptionPercent || 0,
        spaceRestaurantPercent: data.spaceDistribution?.restaurantPercent || 0,
        spaceRetailPercent: data.spaceDistribution?.retailPercent || 0,
        spaceClassroomPercent: data.spaceDistribution?.classroomPercent || 0,
        spaceLibraryPercent: data.spaceDistribution?.libraryPercent || 0,
        spaceLobbyPercent: data.spaceDistribution?.lobbyPercent || 0,
        spaceCafeteriaPercent: data.spaceDistribution?.cafeteriaPercent || 0,
        solarHeatGainFactorBtuPerSqft: data.calculationFactors?.solarHeatGainFactor || 8.0,
        airDensityCorrectionFactor: data.calculationFactors?.airDensityCorrection || 1.0,
        coolingSafetyFactorPercent: data.calculationFactors?.coolingSafetyFactor || 10,
        heatingSafetyFactorPercent: data.calculationFactors?.heatingSafetyFactor || 10
      };

      setVariables(vars);

      // Set IECC and UMC years from report data if available
      if (data.codeCompliance?.ieccYear) {
        setIeccYear(data.codeCompliance.ieccYear as '2018' | '2021');
      }
      if (data.codeCompliance?.umcYear) {
        setUmcYear(data.codeCompliance.umcYear as '2018' | '2021');
      }
    } catch (err) {
      setError('Failed to load HVAC calculation data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [calculationId, authToken]);

  const loadDefaultVariables = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const MEP_SERVICE_URL = API_CONFIG.MEP_SERVICE_URL;
      const response = await fetch(`${MEP_SERVICE_URL}/api/v1/hvac/defaults/5B`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        mode: 'cors'
      });

      if (!response.ok) throw new Error('Failed to load defaults');

      const data = await response.json();
      setVariables(data.defaults);
    } catch (err) {
      setError('Failed to load default variables');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  // Load calculation data
  useEffect(() => {
    if (calculationId) {
      loadCalculationData();
    } else {
      loadDefaultVariables();
    }
  }, [calculationId, loadCalculationData, loadDefaultVariables]);

  const handleVariableChange = (key: keyof HVACVariables, value: number | string) => {
    setModifiedVariables(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveChanges = async () => {
    if (Object.keys(modifiedVariables).length === 0) {
      setError('No changes to save');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const MEP_SERVICE_URL = API_CONFIG.MEP_SERVICE_URL;
      const endpoint = calculationId
        ? `${MEP_SERVICE_URL}/api/v1/hvac/calculate/${calculationId}`
        : `${MEP_SERVICE_URL}/api/v1/hvac/calculate`;

      const method = calculationId ? 'PATCH' : 'POST';

      // Filter out the _codeChange flag from the updates
      const cleanUpdates = Object.entries(modifiedVariables)
        .filter(([key]) => !key.startsWith('_'))
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

      const body = calculationId
        ? {
            updates: cleanUpdates,
            changeReason: 'User adjustment',
            ieccYear,
            umcYear
          }
        : {
            projectId,
            calculationName: `HVAC Calculation - ${new Date().toLocaleDateString()}`,
            variables: { ...variables, ...cleanUpdates },
            ieccYear,
            umcYear
          };

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body),
        mode: 'cors'
      });

      if (!response.ok) throw new Error('Failed to save calculation');

      const data = await response.json();

      if (!calculationId && data.calculationId) {
        // Reload with new calculation ID
        window.location.hash = `#hvac-calculation/${data.calculationId}`;
      }

      setSuccess('Calculation updated successfully');
      setModifiedVariables({});

      // Reload data
      if (calculationId) {
        await loadCalculationData();
      }
    } catch (err) {
      setError('Failed to save changes');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculate = () => {
    handleSaveChanges();
  };

  const handleLocationSearch = async () => {
    if (!locationSearch.trim()) {
      setError('Please enter a ZIP code or city, state');
      return;
    }

    setSearchingLocation(true);
    setError(null);

    try {
      const MEP_SERVICE_URL = API_CONFIG.MEP_SERVICE_URL;
      const response = await fetch(
        `${MEP_SERVICE_URL}/api/v1/hvac/climate-zone/search?query=${encodeURIComponent(locationSearch)}`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          mode: 'cors'
        }
      );

      if (!response.ok) throw new Error('Failed to search location');

      const data = await response.json();

      // Update variables with the found climate zone and conditions
      setModifiedVariables(prev => ({
        ...prev,
        climateZone: data.zone,
        locationCity: data.city,
        locationState: data.state,
        elevationFt: data.conditions.elevation,
        summerOutdoorDbF: data.conditions.summerOutdoorDbF,
        summerOutdoorWbF: data.conditions.summerOutdoorWbF,
        winterOutdoorDbF: data.conditions.winterOutdoorDbF,
      }));

      setSuccess(`Found climate zone ${data.zone} for ${data.city}, ${data.state}`);
    } catch (err) {
      setError('Failed to search location');
      console.error(err);
    } finally {
      setSearchingLocation(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!calculationId) {
      setError('Please save the calculation first');
      return;
    }

    const MEP_SERVICE_URL = API_CONFIG.MEP_SERVICE_URL;
    window.open(`${MEP_SERVICE_URL}/api/v1/hvac/report/${calculationId}/pdf?token=${authToken}`, '_blank');
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const renderVariableSection = (title: string, sectionKey: string, fields: Array<{ key: keyof HVACVariables; label: string; unit?: string }>) => {
    const isExpanded = expandedSections.has(sectionKey);

    return (
      <div style={{ marginBottom: 16 }}>
        <ListItem button onClick={() => toggleSection(sectionKey)}>
          <ListItemText primary={title} />
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItem>
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <div style={{ paddingLeft: 16, paddingRight: 16, paddingBottom: 16 }}>
            {fields.map(field => {
              // Determine if field is text or number
              const isTextField = ['climateZone', 'locationCity', 'locationState'].includes(field.key);

              return (
                <TextField
                  key={field.key}
                  label={field.label}
                  value={(modifiedVariables[field.key] ?? variables?.[field.key]) || ''}
                  onChange={(e) => {
                    const value = isTextField ? e.target.value : (parseFloat(e.target.value) || 0);
                    handleVariableChange(field.key, value as any);
                  }}
                  fullWidth
                  margin="dense"
                  size="small"
                  type={isTextField ? "text" : "number"}
                  InputProps={{
                    endAdornment: field.unit ? <Typography variant="caption">{field.unit}</Typography> : null
                  }}
                />
              );
            })}
          </div>
        </Collapse>
      </div>
    );
  };

  const renderReportTab = () => {
    if (!reportData) return null;

    return (
      <div style={{ padding: 24 }}>
        <Stack spacing={3}>
          {/* Report Header */}
          <div>
            <Typography variant="h5" gutterBottom>
              {reportData.projectName || 'HVAC Load Calculation Report'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {reportData.calculationDate ? new Date(reportData.calculationDate).toLocaleDateString() : new Date().toLocaleDateString()}
            </Typography>
          </div>

          {/* Summary Cards */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <AcUnitIcon color="primary" />
                    <Typography variant="h6">Cooling Load</Typography>
                  </Stack>
                  <Typography variant="h4" color="primary">
                    {(Number(reportData.results?.cooling?.tons) || 0).toFixed(1)} tons
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(Number(reportData.results?.cooling?.totalBtu) || 0).toLocaleString()} BTU/h
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <WhatshotIcon style={{ color: '#d32f2f' }} />
                    <Typography variant="h6">Heating Load</Typography>
                  </Stack>
                  <Typography variant="h4" color="error">
                    {((Number(reportData.results?.heating?.totalBtu) || 0) / 1000).toFixed(0)} MBH
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(Number(reportData.results?.heating?.totalBtu) || 0).toLocaleString()} BTU/h
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    <EngineeringIcon color="action" />
                    <Typography variant="h6">Ventilation</Typography>
                  </Stack>
                  <Typography variant="h4">
                    {(Number(reportData.results?.ventilation?.totalCfm) || 0).toLocaleString()} CFM
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Outside Air Required
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Building Summary */}
          <Paper style={{ padding: 16 }}>
            <Typography variant="h6" gutterBottom>Building Summary</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Gross Area</Typography>
                <Typography>{(Number(reportData.buildingSummary?.grossArea) || 0).toLocaleString()} sq ft</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Net Conditioned Area</Typography>
                <Typography>{(Number(reportData.buildingSummary?.netConditionedArea) || 0).toLocaleString()} sq ft</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Building Perimeter</Typography>
                <Typography>{(Number(reportData.buildingSummary?.perimeter) || 0).toLocaleString()} ft</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Ceiling Height</Typography>
                <Typography>{Number(reportData.buildingSummary?.ceilingHeight) || 0} ft</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Design Conditions */}
          <Paper style={{ padding: 16 }}>
            <Typography variant="h6" gutterBottom>Design Conditions</Typography>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              {reportData.designConditions?.location?.city || 'Unknown'}, {reportData.designConditions?.location?.state || 'Unknown'} -
              Climate Zone {reportData.designConditions?.location?.climateZone || 'Unknown'}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Summer Outdoor</Typography>
                <Typography>
                  {reportData.designConditions?.outdoor?.summerDbF || 0}°F DB / {reportData.designConditions?.outdoor?.summerWbF || 0}°F WB
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Winter Outdoor</Typography>
                <Typography>{reportData.designConditions?.outdoor?.winterDbF || 0}°F</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Indoor Cooling</Typography>
                <Typography>{reportData.designConditions?.indoor?.coolingTempF || 0}°F @ {reportData.designConditions?.indoor?.summerRhPercent || 0}% RH</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Indoor Heating</Typography>
                <Typography>{reportData.designConditions?.indoor?.heatingTempF || 0}°F @ {reportData.designConditions?.indoor?.winterRhPercent || 0}% RH</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Load Components */}
          <Paper style={{ padding: 16 }}>
            <Typography variant="h6" gutterBottom>Cooling Load Components</Typography>
            <Grid container spacing={1}>
              {Object.entries(reportData.results?.cooling?.components || {}).map(([key, value]: [string, any]) => (
                <Grid item xs={6} sm={4} key={key}>
                  <Typography variant="body2" color="text.secondary">
                    {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                  </Typography>
                  <Typography>{(value as number).toLocaleString()} BTU/h</Typography>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Stack>
      </div>
    );
  };

  const renderBreakdownTab = () => {
    if (!variables) {
      return (
        <div style={{ padding: 24 }}>
          <Alert severity="warning">
            No calculation data available. Please perform an HVAC calculation first.
          </Alert>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
        {/* Left side - Adjustable Variables */}
        <div style={{
          width: 400,
          borderRight: '1px solid rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{ padding: 16, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
            <Typography variant="h6">Calculation Variables</Typography>

            {/* Location Search */}
            <div style={{ marginTop: 16, marginBottom: 16 }}>
              <Typography variant="caption" color="text.secondary">
                Search by ZIP code or City, State to set climate zone
              </Typography>
              <Stack direction="row" spacing={1} style={{ marginTop: 8 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="ZIP or City, State"
                  placeholder="e.g., 89109 or Las Vegas, NV"
                  value={locationSearch}
                  onChange={(e) => setLocationSearch(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleLocationSearch();
                    }
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleLocationSearch}
                  disabled={searchingLocation}
                >
                  {searchingLocation ? <CircularProgress size={20} /> : 'Search'}
                </Button>
              </Stack>
            </div>

            {/* Code Year Selection */}
            <Stack direction="row" spacing={2} style={{ marginBottom: 16 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>IECC Year</InputLabel>
                <Select
                  value={ieccYear}
                  label="IECC Year"
                  onChange={(e: SelectChangeEvent) => {
                    setIeccYear(e.target.value as '2018' | '2021');
                    // Mark as modified to trigger recalculation
                    setModifiedVariables(prev => ({ ...prev, _codeChange: true }));
                  }}
                >
                  <MenuItem value="2021">IECC 2021</MenuItem>
                  <MenuItem value="2018">IECC 2018</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>UMC Year</InputLabel>
                <Select
                  value={umcYear}
                  label="UMC Year"
                  onChange={(e: SelectChangeEvent) => {
                    setUmcYear(e.target.value as '2018' | '2021');
                    // Mark as modified to trigger recalculation
                    setModifiedVariables(prev => ({ ...prev, _codeChange: true }));
                  }}
                >
                  <MenuItem value="2018">UMC 2018</MenuItem>
                  <MenuItem value="2021">UMC 2021</MenuItem>
                </Select>
              </FormControl>
            </Stack>

            {Object.keys(modifiedVariables).length > 0 && (
              <Stack direction="row" spacing={1} style={{ marginTop: 8 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveChanges}
                  disabled={saving}
                  fullWidth
                >
                  Recalculate ({Object.keys(modifiedVariables).length} changes)
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setModifiedVariables({})}
                >
                  Reset
                </Button>
              </Stack>
            )}
          </div>

          <List style={{ overflow: 'auto', flex: 1 }}>
            <Card style={{ marginBottom: 24, padding: 16 }}>
              <Typography variant="subtitle1" style={{ marginBottom: 16 }}>Quick Building Type Setup</Typography>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={() => setVariables(prev => prev ? ({
                      ...prev,
                      spaceOfficePercent: 80,
                      spaceConferencePercent: 15,
                      spaceCommonPercent: 5,
                      spaceReceptionPercent: 0,
                      spaceRestaurantPercent: 0,
                      spaceRetailPercent: 0,
                      spaceClassroomPercent: 0,
                      spaceLibraryPercent: 0,
                      spaceLobbyPercent: 0,
                      spaceCafeteriaPercent: 0
                    }) : prev)}
                  >
                    Office
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={() => setVariables(prev => prev ? ({
                      ...prev,
                      spaceOfficePercent: 0,
                      spaceConferencePercent: 0,
                      spaceCommonPercent: 10,
                      spaceReceptionPercent: 5,
                      spaceRestaurantPercent: 70,
                      spaceRetailPercent: 0,
                      spaceClassroomPercent: 0,
                      spaceLibraryPercent: 0,
                      spaceLobbyPercent: 10,
                      spaceCafeteriaPercent: 5
                    }) : prev)}
                  >
                    Restaurant
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={() => setVariables(prev => prev ? ({
                      ...prev,
                      spaceOfficePercent: 10,
                      spaceConferencePercent: 5,
                      spaceCommonPercent: 5,
                      spaceReceptionPercent: 5,
                      spaceRestaurantPercent: 0,
                      spaceRetailPercent: 60,
                      spaceClassroomPercent: 0,
                      spaceLibraryPercent: 0,
                      spaceLobbyPercent: 10,
                      spaceCafeteriaPercent: 5
                    }) : prev)}
                  >
                    Retail
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={() => setVariables(prev => prev ? ({
                      ...prev,
                      spaceOfficePercent: 10,
                      spaceConferencePercent: 10,
                      spaceCommonPercent: 15,
                      spaceReceptionPercent: 5,
                      spaceRestaurantPercent: 0,
                      spaceRetailPercent: 0,
                      spaceClassroomPercent: 45,
                      spaceLibraryPercent: 10,
                      spaceLobbyPercent: 5,
                      spaceCafeteriaPercent: 0
                    }) : prev)}
                  >
                    Educational
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={() => setVariables(prev => prev ? ({
                      ...prev,
                      spaceOfficePercent: 15,
                      spaceConferencePercent: 15,
                      spaceCommonPercent: 10,
                      spaceReceptionPercent: 10,
                      spaceRestaurantPercent: 0,
                      spaceRetailPercent: 0,
                      spaceClassroomPercent: 0,
                      spaceLibraryPercent: 35,
                      spaceLobbyPercent: 10,
                      spaceCafeteriaPercent: 5
                    }) : prev)}
                  >
                    Library
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={() => setVariables(prev => prev ? ({
                      ...prev,
                      spaceOfficePercent: 30,
                      spaceConferencePercent: 20,
                      spaceCommonPercent: 15,
                      spaceReceptionPercent: 10,
                      spaceRestaurantPercent: 5,
                      spaceRetailPercent: 5,
                      spaceClassroomPercent: 0,
                      spaceLibraryPercent: 0,
                      spaceLobbyPercent: 10,
                      spaceCafeteriaPercent: 5
                    }) : prev)}
                  >
                    Mixed Use
                  </Button>
                </Grid>
              </Grid>
            </Card>

            {renderVariableSection('Building Parameters', 'building', [
              { key: 'grossAreaSqft', label: 'Gross Area', unit: 'sq ft' },
              { key: 'netConditionedAreaSqft', label: 'Net Conditioned Area', unit: 'sq ft' },
              { key: 'buildingPerimeterFt', label: 'Building Perimeter', unit: 'ft' },
              { key: 'ceilingHeightFt', label: 'Ceiling Height', unit: 'ft' }
            ])}

            {renderVariableSection('Climate Conditions', 'climate', [
              { key: 'climateZone', label: 'Climate Zone' },
              { key: 'locationCity', label: 'City' },
              { key: 'locationState', label: 'State' },
              { key: 'elevationFt', label: 'Elevation', unit: 'ft' },
              { key: 'summerOutdoorDbF', label: 'Summer Outdoor DB', unit: '°F' },
              { key: 'summerOutdoorWbF', label: 'Summer Outdoor WB', unit: '°F' },
              { key: 'winterOutdoorDbF', label: 'Winter Outdoor DB', unit: '°F' },
              { key: 'indoorCoolingTempF', label: 'Indoor Cooling Temp', unit: '°F' },
              { key: 'indoorHeatingTempF', label: 'Indoor Heating Temp', unit: '°F' },
              { key: 'indoorSummerRhPercent', label: 'Indoor Summer RH', unit: '%' },
              { key: 'indoorWinterRhPercent', label: 'Indoor Winter RH', unit: '%' }
            ])}

            {renderVariableSection('Envelope', 'envelope', [
              { key: 'wallRValue', label: 'Wall R-Value', unit: 'R' },
              { key: 'wallUValue', label: 'Wall U-Value', unit: 'U' },
              { key: 'roofRValue', label: 'Roof R-Value', unit: 'R' },
              { key: 'roofUValue', label: 'Roof U-Value', unit: 'U' },
              { key: 'windowUValue', label: 'Window U-Value', unit: 'U' },
              { key: 'windowShgc', label: 'Window SHGC' },
              { key: 'windowToWallRatio', label: 'Window-to-Wall Ratio', unit: '%' },
              { key: 'infiltrationAch', label: 'Infiltration', unit: 'ACH' }
            ])}

            {renderVariableSection('Internal Gains', 'gains', [
              { key: 'occupancyDensityPer1000Sqft', label: 'Occupancy Density', unit: '/1000sf' },
              { key: 'occupancySensibleBtuPerPerson', label: 'Occupancy Sensible', unit: 'BTU/person' },
              { key: 'occupancyLatentBtuPerPerson', label: 'Occupancy Latent', unit: 'BTU/person' },
              { key: 'lightingWattsPerSqft', label: 'Lighting', unit: 'W/sf' },
              { key: 'equipmentWattsPerSqft', label: 'Equipment', unit: 'W/sf' }
            ])}

            {renderVariableSection('Ventilation - ASHRAE 62.1', 'ventilation', [
              { key: 'ventilationOfficeCfmPerPerson', label: 'Office CFM/Person', unit: 'CFM' },
              { key: 'ventilationOfficeCfmPerSqft', label: 'Office CFM/SqFt', unit: 'CFM' },
              { key: 'ventilationConferenceCfmPerPerson', label: 'Conference CFM/Person', unit: 'CFM' },
              { key: 'ventilationConferenceCfmPerSqft', label: 'Conference CFM/SqFt', unit: 'CFM' },
              { key: 'ventilationCommonCfmPerSqft', label: 'Common CFM/SqFt', unit: 'CFM' },
              { key: 'ventilationReceptionCfmPerPerson', label: 'Reception CFM/Person', unit: 'CFM' },
              { key: 'ventilationReceptionCfmPerSqft', label: 'Reception CFM/SqFt', unit: 'CFM' },
              { key: 'ventilationRestaurantCfmPerPerson', label: 'Restaurant CFM/Person', unit: 'CFM' },
              { key: 'ventilationRestaurantCfmPerSqft', label: 'Restaurant CFM/SqFt', unit: 'CFM' },
              { key: 'ventilationRetailCfmPerPerson', label: 'Retail CFM/Person', unit: 'CFM' },
              { key: 'ventilationRetailCfmPerSqft', label: 'Retail CFM/SqFt', unit: 'CFM' },
              { key: 'ventilationClassroomCfmPerPerson', label: 'Classroom CFM/Person', unit: 'CFM' },
              { key: 'ventilationClassroomCfmPerSqft', label: 'Classroom CFM/SqFt', unit: 'CFM' },
              { key: 'ventilationLibraryCfmPerPerson', label: 'Library CFM/Person', unit: 'CFM' },
              { key: 'ventilationLibraryCfmPerSqft', label: 'Library CFM/SqFt', unit: 'CFM' },
              { key: 'ventilationLobbysCfmPerPerson', label: 'Lobby CFM/Person', unit: 'CFM' },
              { key: 'ventilationLobbysCfmPerSqft', label: 'Lobby CFM/SqFt', unit: 'CFM' },
              { key: 'ventilationCafeteriaCfmPerPerson', label: 'Cafeteria CFM/Person', unit: 'CFM' },
              { key: 'ventilationCafeteriaCfmPerSqft', label: 'Cafeteria CFM/SqFt', unit: 'CFM' }
            ])}

            {renderVariableSection('Space Distribution', 'space', [
              { key: 'spaceOfficePercent', label: 'Office Space', unit: '%' },
              { key: 'spaceConferencePercent', label: 'Conference Space', unit: '%' },
              { key: 'spaceCommonPercent', label: 'Common Space', unit: '%' },
              { key: 'spaceReceptionPercent', label: 'Reception Space', unit: '%' },
              { key: 'spaceRestaurantPercent', label: 'Restaurant Space', unit: '%' },
              { key: 'spaceRetailPercent', label: 'Retail Space', unit: '%' },
              { key: 'spaceClassroomPercent', label: 'Classroom Space', unit: '%' },
              { key: 'spaceLibraryPercent', label: 'Library Space', unit: '%' },
              { key: 'spaceLobbyPercent', label: 'Lobby Space', unit: '%' },
              { key: 'spaceCafeteriaPercent', label: 'Cafeteria Space', unit: '%' }
            ])}

            {renderVariableSection('Safety Factors', 'safety', [
              { key: 'coolingSafetyFactorPercent', label: 'Cooling Safety Factor', unit: '%' },
              { key: 'heatingSafetyFactorPercent', label: 'Heating Safety Factor', unit: '%' },
              { key: 'solarHeatGainFactorBtuPerSqft', label: 'Solar Heat Gain', unit: 'BTU/sf' },
              { key: 'airDensityCorrectionFactor', label: 'Air Density Correction' }
            ])}
          </List>
        </div>

        {/* Right side - Breakdown Results */}
        <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
          {reportData ? (
            <Stack spacing={3}>
              <Typography variant="h5">Calculation Breakdown</Typography>

              {/* Cooling Load Breakdown */}
              <Paper style={{ padding: 16 }}>
                <Typography variant="h6" gutterBottom>
                  Cooling Load Components
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(reportData.results?.cooling?.components || {}).map(([key, value]: [string, any]) => (
                    <Grid item xs={6} key={key}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                        </Typography>
                        <Typography variant="body2">
                          {(Number(value) || 0).toLocaleString()} BTU/h
                        </Typography>
                      </Stack>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="subtitle1">
                        <strong>Total Cooling Load</strong>
                      </Typography>
                      <Typography variant="subtitle1">
                        <strong>{(Number(reportData.results?.cooling?.totalBtu) || 0).toLocaleString()} BTU/h</strong>
                        ({(Number(reportData.results?.cooling?.tons) || 0).toFixed(1)} tons)
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              {/* Heating Load Breakdown */}
              <Paper style={{ padding: 16 }}>
                <Typography variant="h6" gutterBottom>
                  Heating Load Components
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(reportData.results?.heating?.components || {}).map(([key, value]: [string, any]) => (
                    <Grid item xs={6} key={key}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                        </Typography>
                        <Typography variant="body2">
                          {(Number(value) || 0).toLocaleString()} BTU/h
                        </Typography>
                      </Stack>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="subtitle1">
                        <strong>Total Heating Load</strong>
                      </Typography>
                      <Typography variant="subtitle1">
                        <strong>{(Number(reportData.results?.heating?.totalBtu) || 0).toLocaleString()} BTU/h</strong>
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              {/* Ventilation Requirements */}
              <Paper style={{ padding: 16 }}>
                <Typography variant="h6" gutterBottom>
                  Ventilation Requirements
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Total Ventilation Required:
                    </Typography>
                    <Typography variant="body2">
                      {(Number(reportData.results?.ventilation?.totalCfm) || 0).toLocaleString()} CFM
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Ventilation per Square Foot:
                    </Typography>
                    <Typography variant="body2">
                      {((Number(reportData.results?.ventilation?.totalCfm) || 0) / (Number(variables?.grossAreaSqft) || 1)).toFixed(3)} CFM/sf
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
          ) : (
            <Alert severity="info">
              Perform a calculation to see the breakdown results
            </Alert>
          )}
        </div>
      </div>
    );
  };

  return (
    <Paper
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div style={{
        padding: 16,
        borderBottom: '1px solid rgba(0,0,0,0.12)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton onClick={() => setDrawerOpen(!drawerOpen)} size="small">
            <MenuIcon />
          </IconButton>
          <Typography variant="h6">HVAC Load Calculation</Typography>
          {calculationId && (
            <Chip label={`ID: ${calculationId.substring(0, 8)}`} size="small" />
          )}
        </Stack>

        <Stack direction="row" spacing={1}>
          <Tooltip title="Download PDF Report">
            <IconButton onClick={handleDownloadPDF} disabled={!calculationId}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Recalculate">
            <IconButton onClick={handleRecalculate} disabled={Object.keys(modifiedVariables).length === 0}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="View History">
            <IconButton disabled={!calculationId}>
              <HistoryIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </div>

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Main Content */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Variables Drawer */}
        <Drawer
          variant="persistent"
          anchor="left"
          open={drawerOpen}
          sx={{
            width: drawerOpen ? 320 : 0,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 320,
              position: 'relative',
              height: '100%'
            }
          }}
        >
          <div style={{ padding: 16, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
            <Typography variant="h6">Variables</Typography>
            {Object.keys(modifiedVariables).length > 0 && (
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                onClick={handleSaveChanges}
                disabled={saving}
                fullWidth
                style={{ marginTop: 8 }}
              >
                Save Changes ({Object.keys(modifiedVariables).length})
              </Button>
            )}
          </div>

          <List style={{ overflow: 'auto' }}>
            {renderVariableSection('Building Parameters', 'building', [
              { key: 'grossAreaSqft', label: 'Gross Area', unit: 'sq ft' },
              { key: 'netConditionedAreaSqft', label: 'Net Conditioned Area', unit: 'sq ft' },
              { key: 'buildingPerimeterFt', label: 'Building Perimeter', unit: 'ft' },
              { key: 'ceilingHeightFt', label: 'Ceiling Height', unit: 'ft' }
            ])}

            {renderVariableSection('Climate Conditions', 'climate', [
              { key: 'summerOutdoorDbF', label: 'Summer Outdoor DB', unit: '°F' },
              { key: 'summerOutdoorWbF', label: 'Summer Outdoor WB', unit: '°F' },
              { key: 'winterOutdoorDbF', label: 'Winter Outdoor DB', unit: '°F' },
              { key: 'indoorCoolingTempF', label: 'Indoor Cooling Temp', unit: '°F' },
              { key: 'indoorHeatingTempF', label: 'Indoor Heating Temp', unit: '°F' }
            ])}

            {renderVariableSection('Envelope', 'envelope', [
              { key: 'wallRValue', label: 'Wall R-Value', unit: 'R' },
              { key: 'roofRValue', label: 'Roof R-Value', unit: 'R' },
              { key: 'windowUValue', label: 'Window U-Value', unit: 'U' },
              { key: 'windowShgc', label: 'Window SHGC' },
              { key: 'windowToWallRatio', label: 'Window-to-Wall Ratio', unit: '%' },
              { key: 'infiltrationAch', label: 'Infiltration', unit: 'ACH' }
            ])}

            {renderVariableSection('Internal Gains', 'gains', [
              { key: 'occupancyDensityPer1000Sqft', label: 'Occupancy Density', unit: '/1000sf' },
              { key: 'lightingWattsPerSqft', label: 'Lighting', unit: 'W/sf' },
              { key: 'equipmentWattsPerSqft', label: 'Equipment', unit: 'W/sf' }
            ])}

            {renderVariableSection('Safety Factors', 'safety', [
              { key: 'coolingSafetyFactorPercent', label: 'Cooling Safety Factor', unit: '%' },
              { key: 'heatingSafetyFactorPercent', label: 'Heating Safety Factor', unit: '%' }
            ])}
          </List>
        </Drawer>

        {/* Report Area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} style={{ borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
            <Tab label="Report" />
            <Tab label="Breakdown" />
            <Tab label="Viewer" icon={<ArchitectureIcon />} iconPosition="start" />
          </Tabs>

          <div style={{ flex: 1, overflow: 'auto' }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </div>
            ) : (
              <>
                {activeTab === 0 && renderReportTab()}
                {activeTab === 1 && renderBreakdownTab()}
                {activeTab === 2 && (
                  <div style={{ height: '100%', padding: 16 }}>
                    <DXFViewer
                      projectId={projectId}
                      embedded={true}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Paper>
  );
};

export default HVACCalculationPanel;
